import { AlertCircle, CheckCircle2, ClipboardPaste, Download, FileSpreadsheet, UploadCloud, X } from "lucide-react";
import { ChangeEvent, DragEvent, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { ClassSummary, StudentImportResult, StudentImportRow } from "../types";

type Props = {
  classItem: ClassSummary;
  onClose: () => void;
  onImported: (result: StudentImportResult) => void;
};

function normalizeHeader(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[đĐ]/g, "d").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseMatrix(matrix: unknown[][]): StudentImportRow[] {
  const nonEmpty = matrix.filter((row) => row.some((cell) => String(cell ?? "").trim()));
  if (!nonEmpty.length) return [];
  let start = 0;
  const firstHeaders = nonEmpty[0].map((cell) => normalizeHeader(String(cell ?? "")));
  const hasHeader = firstHeaders.some((header) => ["sbd", "sobaodanh", "mahocsinh", "hovaten", "hovatenlot", "hoten", "ten"].includes(header));
  let codeIndex = 0;
  let lastNameIndex = -1;
  let firstNameIndex = -1;
  let fullNameIndex = -1;
  if (hasHeader) {
    start = 1;
    codeIndex = firstHeaders.findIndex((header) => ["sbd", "sobaodanh", "mahocsinh", "studentcode"].includes(header));
    lastNameIndex = firstHeaders.findIndex((header) => header === "hovatenlot");
    firstNameIndex = firstHeaders.findIndex((header) => header === "ten");
    fullNameIndex = firstHeaders.findIndex((header) => ["hovaten", "hoten"].includes(header));
    if (codeIndex < 0) codeIndex = 0;
  } else if ((nonEmpty[0]?.length || 0) >= 3) {
    lastNameIndex = 1;
    firstNameIndex = 2;
  } else {
    fullNameIndex = 1;
  }

  return nonEmpty.slice(start).map((row) => ({
    studentCode: String(row[codeIndex] ?? "").trim(),
    lastName: lastNameIndex >= 0 ? String(row[lastNameIndex] ?? "").trim() : undefined,
    firstName: firstNameIndex >= 0 ? String(row[firstNameIndex] ?? "").trim() : undefined,
    fullName: fullNameIndex >= 0 ? String(row[fullNameIndex] ?? "").trim() : undefined,
  })).filter((row) => row.studentCode);
}

function splitPreview(row: StudentImportRow) {
  if (row.lastName && row.firstName) return { lastName: row.lastName, firstName: row.firstName };
  const parts = (row.fullName || "").trim().replace(/\s+/g, " ").split(" ").filter(Boolean);
  return parts.length >= 2 ? { lastName: parts.slice(0, -1).join(" "), firstName: parts.at(-1) || "" } : { lastName: "", firstName: "" };
}

export default function StudentImportModal({ classItem, onClose, onImported }: Props) {
  const [mode, setMode] = useState<"file" | "clipboard">("file");
  const [rows, setRows] = useState<StudentImportRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [clipboard, setClipboard] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const preview = useMemo(() => rows.map((row) => ({ ...row, ...splitPreview(row) })), [rows]);
  const invalidCount = preview.filter((row) => !row.studentCode || !row.lastName || !row.firstName).length;

  const parseFile = async (file: File) => {
    setError("");
    setRows([]);
    setFileName(file.name);
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      let parsed: StudentImportRow[] = [];
      for (const name of workbook.SheetNames) {
        const sheet = workbook.Sheets[name];
        if (!sheet) continue;
        parsed = parseMatrix(XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: false }));
        if (parsed.length) break;
      }
      if (!parsed.length) throw new Error("Không tìm thấy danh sách học sinh trong file.");
      setRows(parsed);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể đọc file này.");
    }
  };

  const parseClipboard = () => {
    const matrix = clipboard.split(/\r?\n/).map((line) => line.split("\t"));
    const parsed = parseMatrix(matrix);
    if (!parsed.length) setError("Chưa tìm thấy dữ liệu. Hãy sao chép 2 hoặc 3 cột từ Excel/Google Sheets.");
    else { setError(""); setRows(parsed); }
  };

  const downloadTemplate = async () => {
    const XLSX = await import("xlsx");
    const sheet = XLSX.utils.aoa_to_sheet([["SBD", "Họ và tên lót", "Tên"], ["", "", ""]]);
    sheet["!cols"] = [{ wch: 16 }, { wch: 28 }, { wch: 16 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Danh sách học sinh");
    XLSX.writeFile(workbook, `Mau_danh_sach_${classItem.name}.xlsx`);
  };

  const submit = async () => {
    if (!rows.length || invalidCount) return;
    setSubmitting(true);
    setError("");
    try {
      onImported(await api.importStudents(classItem.id, rows));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể nhập danh sách học sinh.");
    } finally {
      setSubmitting(false);
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void parseFile(file);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) void parseFile(file);
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="import-modal student-import-modal" role="dialog" aria-modal="true" aria-labelledby="student-import-title">
        <header>
          <div><span className="modal-kicker">{classItem.name.toUpperCase()}</span><h2 id="student-import-title">Nhập danh sách học sinh</h2><p>Hỗ trợ 2 cột SBD + Họ và tên, hoặc 3 cột SBD + Họ và tên lót + Tên.</p></div>
          <button className="icon-button" onClick={onClose} aria-label="Đóng"><X /></button>
        </header>
        <div className="import-mode-tabs">
          <button className={mode === "file" ? "active" : ""} onClick={() => { setMode("file"); setRows([]); setError(""); }}><FileSpreadsheet />Từ file Excel</button>
          <button className={mode === "clipboard" ? "active" : ""} onClick={() => { setMode("clipboard"); setRows([]); setError(""); }}><ClipboardPaste />Từ clipboard</button>
        </div>
        <div className="student-import-body">
          <div className="student-import-help"><b>Định dạng tự động</b><span>Với dữ liệu 2 cột, hệ thống lấy chữ cuối cùng làm tên học sinh.</span><button className="template-button" onClick={() => void downloadTemplate()}><Download /> Tải file mẫu 3 cột</button></div>
          {mode === "file" ? (
            <div className={`drop-zone ${rows.length ? "drop-zone--ready" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={onDrop} onClick={() => inputRef.current?.click()}>
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange} hidden />
              {rows.length ? <CheckCircle2 className="drop-success" /> : <UploadCloud />}
              <strong>{rows.length ? fileName : "Kéo thả file danh sách vào đây"}</strong>
              <span>{rows.length ? `${rows.length} học sinh đã đọc` : "hoặc bấm để chọn .xlsx, .xls, .csv"}</span>
            </div>
          ) : (
            <div className="clipboard-box"><textarea value={clipboard} onChange={(event) => setClipboard(event.target.value)} placeholder={`SBD\tHọ và tên\n99112001\tNguyễn Văn An\n99112002\tTrần Thị Bình`} /><button onClick={parseClipboard}><ClipboardPaste />Đọc dữ liệu đã dán</button></div>
          )}
        </div>
        {error && <div className="import-alert import-alert--error student-import-alert"><AlertCircle /> <span>{error}</span></div>}
        {rows.length > 0 && (
          <div className="import-preview student-preview">
            <div className="import-preview__head"><div><FileSpreadsheet /><span><b>Xem trước danh sách</b><small>{rows.length - invalidCount} hợp lệ · {invalidCount} lỗi</small></span></div>{!invalidCount && <span className="valid-badge"><CheckCircle2 /> Sẵn sàng</span>}</div>
            <div className="preview-table-wrap"><table><thead><tr><th>SBD</th><th>Họ và tên lót</th><th>Tên</th></tr></thead><tbody>{preview.slice(0, 6).map((row, index) => <tr key={`${row.studentCode}-${index}`} className={!row.lastName || !row.firstName ? "row-invalid" : ""}><td><b>{row.studentCode}</b></td><td>{row.lastName || "Thiếu dữ liệu"}</td><td>{row.firstName || "—"}</td></tr>)}</tbody></table>{rows.length > 6 && <div className="preview-more">và {rows.length - 6} học sinh khác…</div>}</div>
          </div>
        )}
        <footer><button className="btn-secondary" onClick={onClose}>Hủy</button><button className="btn-primary-small" disabled={!rows.length || invalidCount > 0 || submitting} onClick={() => void submit()}>{submitting ? <span className="spinner" /> : <><UploadCloud />Nhập {rows.length || ""} học sinh</>}</button></footer>
      </section>
    </div>
  );
}
